import { useRef, useImperativeHandle, forwardRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

/**
 * TurnstileWidget
 * ---------------
 * Reusable Cloudflare Turnstile widget used on Login / Register / ForgotPassword.
 *
 * • Managed mode (Cloudflare auto-decides invisible vs interactive).
 * • Calls `onToken(token)` when Turnstile emits a fresh challenge token.
 * • Calls `onToken(null)` on expire / error so the parent's Submit button
 *   can stay disabled until we have a fresh token.
 * • Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is unset — the backend
 *   also fails-open in that case, so dev / preview keeps working.
 * • Exposes an imperative `.reset()` handle so the parent can force a new
 *   token after a failed submit (tokens are single-use).
 *
 * NOTE ON CAPACITOR (iOS + Android native):
 * Both platforms present hostname `localhost` to Cloudflare (iOS webview uses
 * `capacitor://localhost`, Android uses `https://localhost`). As long as
 * `localhost` is in the Turnstile widget's hostname allow-list, the widget
 * renders and validates identically to the web build.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget({ onToken, action }, ref) {
  const inner = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      try { inner.current?.reset(); } catch { /* noop */ }
      onToken?.(null);
    },
  }), [onToken]);

  if (!SITE_KEY) return null;

  return (
    <div className="flex justify-center py-2" data-testid="turnstile-widget">
      <Turnstile
        ref={inner}
        siteKey={SITE_KEY}
        options={{
          size: "flexible",
          action: action || "auth",
          refreshExpired: "auto",
          theme: "light",
        }}
        onSuccess={(t) => onToken?.(t)}
        onError={() => onToken?.(null)}
        onExpire={() => onToken?.(null)}
      />
    </div>
  );
});

export default TurnstileWidget;
